import { getApiBase } from '@/lib/api';

/**
 * Public (unauthenticated) client for the external read-only chat share.
 * Mirrors `public-chat.client.ts`: plain `fetch`, no credentials/auth header.
 * The whole surface is read-only — there is no send/cancel path.
 */

export interface IExternalShareInfo {
  valid: boolean;
  requiresPin: boolean;
}

export interface IExternalShareAuthResponse {
  authenticated: boolean;
  viewToken: string | null;
  error: string | null;
}

export interface IExternalShareMessage {
  id: string;
  sender: string;
  content: string;
  // The message this one follows; siblings are the versions of one place (KAN-1961).
  parentId?: string | null;
  contentBlocks?: Record<string, unknown>[] | null;
  // Whether the history was summarised during this answer (KAN-1706).
  compacted?: boolean;
  attachments?: Record<string, unknown>[];
}

export interface IExternalShareTranscript {
  title: string;
  messages: IExternalShareMessage[];
  hasActiveRun: boolean;
  // The message the live answer stands under, when the API knows it.
  runParentId?: string | null;
}

// A read that gets no answer must fail, or nothing notices a server that went silent.
const TRANSCRIPT_TIMEOUT_MS = 10_000;

const base = (token: string) => `${getApiBase()}/api/v1/public-share/${encodeURIComponent(token)}`;

/** Pre-auth info: whether the link is valid and needs a PIN. No content. */
export async function fetchExternalShareInfo(token: string): Promise<IExternalShareInfo | null> {
  const res = await fetch(base(token), {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) return null;
  return res.json() as Promise<IExternalShareInfo>;
}

/** Submit the PIN; on success returns a short-lived view token. */
export async function authenticateExternalShare(
  token: string,
  pin: string
): Promise<IExternalShareAuthResponse> {
  const res = await fetch(`${base(token)}/auth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ pin }),
  });
  if (!res.ok) {
    let detail = `Fehler: ${res.status}`;
    try {
      const body = await res.json();
      if (body.detail)
        detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch {
      // non-JSON body
    }
    throw new Error(detail);
  }
  return res.json() as Promise<IExternalShareAuthResponse>;
}

/**
 * Whether a 404 comes from our API. During a deploy the ingress answers 404 too
 * while it has no route, in plain text or empty: that says nothing about the link.
 */
async function isApiNotFound(res: Response): Promise<boolean> {
  try {
    const body = await res.json();
    return body?.error?.code === 'HTTP_404';
  } catch {
    return false;
  }
}

/**
 * Fetch the read-only transcript (requires a valid view token). Throws
 * `unauthenticated` when the PIN has to be entered again and `gone` when our API
 * says the link was withdrawn or has expired.
 */
export async function fetchExternalShareTranscript(
  token: string,
  viewToken: string
): Promise<IExternalShareTranscript> {
  const res = await fetch(`${base(token)}/transcript`, {
    headers: { Accept: 'application/json', 'X-Share-View-Token': viewToken },
    cache: 'no-store',
    signal: AbortSignal.timeout(TRANSCRIPT_TIMEOUT_MS),
  });
  if (res.status === 401) throw new Error('unauthenticated');
  if (res.status === 404 && (await isApiNotFound(res))) throw new Error('gone');
  if (!res.ok) throw new Error(`Fehler: ${res.status}`);
  return res.json() as Promise<IExternalShareTranscript>;
}

/** URL of the read-only live SSE stream (view token as query param). */
export function externalShareStreamUrl(token: string, viewToken: string): string {
  return `${base(token)}/stream?viewToken=${encodeURIComponent(viewToken)}`;
}

/**
 * URL of one upload shown in the shared transcript, addressed by its message
 * and its position in that message's attachment list — the share API publishes
 * no file ids, so position is how the viewer names a file.
 */
export function externalShareAttachmentUrl(
  token: string,
  viewToken: string,
  messageId: string,
  index: number
): string {
  return `${base(token)}/attachments/${encodeURIComponent(messageId)}/${index}?viewToken=${encodeURIComponent(viewToken)}`;
}

/**
 * URL that serves an in-chat file link (generated image, written document)
 * through the share gate. `src` is the original URL as it appears in the
 * message; the backend only honours it for files this very chat references.
 */
export function externalShareMediaUrl(token: string, viewToken: string, src: string): string {
  // Parentheses stay literal through encodeURIComponent and would terminate a
  // markdown link early, so they are escaped explicitly.
  const encodedSrc = encodeURIComponent(src).replace(/\(/g, '%28').replace(/\)/g, '%29');
  return `${base(token)}/media?src=${encodedSrc}&viewToken=${encodeURIComponent(viewToken)}`;
}
